import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchBranchList,
  fetchDoctorList,
  fetchSalespersonList,
  fetchUserList,
} from '../utils/getApis';
import { queryKeys } from './queryKeys';
import { useCachePolicySettings } from './useCachePolicySettings';

const toMs = (seconds, fallbackSeconds) => {
  const parsed = Number.parseInt(seconds, 10);
  const safe = Number.isFinite(parsed) ? parsed : fallbackSeconds;
  return Math.max(10, safe) * 1000;
};

export const useReferenceTtlMs = (currentUserInfo) => {
  const { data } = useCachePolicySettings(currentUserInfo);
  return useMemo(() => toMs(data?.reference_data_ttl_seconds, 600), [data?.reference_data_ttl_seconds]);
};

export const useBranchListQuery = (currentUserInfo, options = {}) => {
  const ttlMs = useReferenceTtlMs(currentUserInfo);
  return useQuery({
    queryKey: queryKeys.branchList(currentUserInfo?.uid),
    queryFn: () => fetchBranchList(currentUserInfo),
    enabled: Boolean(currentUserInfo?.uid) && (options.enabled ?? true),
    staleTime: ttlMs,
    gcTime: 20 * 60 * 1000,
  });
};

export const useDoctorListQuery = (currentUserInfo, options = {}) => {
  const ttlMs = useReferenceTtlMs(currentUserInfo);
  return useQuery({
    queryKey: queryKeys.doctorList(currentUserInfo?.uid),
    queryFn: () => fetchDoctorList(currentUserInfo),
    enabled: Boolean(currentUserInfo?.uid) && (options.enabled ?? true),
    staleTime: ttlMs,
    gcTime: 20 * 60 * 1000,
  });
};

export const useSalespersonListQuery = (currentUserInfo, options = {}) => {
  const ttlMs = useReferenceTtlMs(currentUserInfo);
  return useQuery({
    queryKey: queryKeys.salespersonList(currentUserInfo?.uid),
    queryFn: () => fetchSalespersonList(currentUserInfo),
    enabled: Boolean(currentUserInfo?.uid) && (options.enabled ?? true),
    staleTime: ttlMs,
    gcTime: 20 * 60 * 1000,
  });
};

export const useUserListQuery = (currentUserInfo, options = {}) => {
  const ttlMs = useReferenceTtlMs(currentUserInfo);
  return useQuery({
    queryKey: ['reference', 'users', currentUserInfo?.uid || 'anonymous'],
    queryFn: () => fetchUserList(currentUserInfo),
    enabled: Boolean(currentUserInfo?.uid) && (options.enabled ?? true),
    staleTime: ttlMs,
    gcTime: 20 * 60 * 1000,
  });
};
